import styled from "@emotion/styled";
import theme from "../../theme";
import { GlassIcon, RemoveButton } from "../../assets/icons/icons";
import { Button } from "./Button";
import { useWindowSize } from "@reactuses/core";
import { InputHTMLAttributes, memo } from "react";
import { randomInt } from "../../utils/util";
import { SrLable } from "./SrLabel";
import { useI18n } from "../../locales";
const { md } = theme.screen;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  placeholder: string;
  onSubmit: any;
  onChange: any;
  inputValue: string;
  setInputValue: any;
  bottom?: string; // 값 없으면 -9px 적용됨
  width?: string;
  direction?: "row" | "column"; // input/button 나열 방향
  startFetch?: () => void;
  gap?: string;
  marginR?: string;
}

export const SearchForm = memo(
  ({
    placeholder,
    onSubmit,
    onChange,
    inputValue,
    setInputValue,
    bottom = "-9px",
    width = "263px",
    direction = "row",
    startFetch,
    gap,
    marginR,
    ...inputProps
  }: Props) => {
    const { t } = useI18n();
    const { width: windowWidth } = useWindowSize();

    // 라벨이랑 인풋 연결하는 id (랜덤 난수값)
    const id = String(randomInt());

    return (
      <CustomForm
        onSubmit={onSubmit}
        direction={direction}
        bottom={bottom}
        gap={gap}
      >
        <div style={{ position: "relative", width: width }}>
          <SrLable id={id} labelText={placeholder} />
          <CustomInput
            id={id}
            placeholder={placeholder}
            onChange={onChange}
            value={inputValue}
            width={width}
            style={{
              marginRight: marginR ? marginR : "10px",
            }}
            {...inputProps}
          />
          <IconWrapper>
            <GlassIcon />
          </IconWrapper>
          <RemoveButtonWrapper
            type="button"
            onClick={() => setInputValue("")}
          >
            <RemoveButton />
          </RemoveButtonWrapper>
        </div>
        <BtnContainer
          direction={direction}
          onClick={() => {
            if (startFetch) {
              startFetch();
            }
          }}
        >
          <Button
            type="submit"
            bgColor={theme.statusColor.MAIN}
            hoverColor={theme.color.green_600}
            width={windowWidth > md ? "56px" : `42px`}
            height={windowWidth > md ? "40px" : `30px`}
            paddingX="0px"
          >
            {t.common.search}
          </Button>
        </BtnContainer>
      </CustomForm>
    );
  }
);

const CustomForm = styled.form<{
  direction?: string;
  bottom?: string;
  gap?: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ gap }) => (gap ? gap : "10px")};
  bottom: ${({ bottom }) => bottom};
  flex-direction: ${({ direction }) => direction};
`;

const CustomInput = styled.input<{ width: string }>`
  background: ${theme.color.white};
  border-radius: 3px;
  height: 40px;
  border: none;
  padding-left: 40px;
  padding-right: 30px;
  font-size: ${theme.fontSize.FONT_1};
  font-weight: 500;
  line-height: 19px;
  width: ${({ width }) => width};

  @media (max-width: ${md}px) {
    padding: 7px 10px;
    padding-left: 27px;
    padding-right: 22px;
    height: 30px;
    font-size: ${theme.fontSize.FONT_2};
    ::placeholder {
      text-align: center;
    }
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 13.65px;

  @media (max-width: ${md}px) {
    left: 10px;
    height: 15px;
    width: 15px;
    svg {
      height: 15px;
      width: 15px;
    }
  }
`;

const RemoveButtonWrapper = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 8px;
  cursor: pointer;

  @media (max-width: ${md}px) {
    svg {
      width: 13px;
      height: 12px;
    }
  }
`;

const BtnContainer = styled.div<{ direction: string }>`
  display: flex;
  width: ${({ direction }) => (direction == "column" ? "100%" : "auto")};

  button {
    flex-grow: ${({ direction }) => (direction == "column" ? "1" : "0")};
  }
`;
